/*
 * One answer, readable at two depths.
 *
 * This replaced four tabs — TL;DR / Visual / Detail / Full visual — which split
 * every answer along two axes at once: how much, and words-or-picture. The
 * second split was the mistake. A diagram is not an alternative to its
 * explanation, it is part of it, and putting them behind separate tabs meant
 * whichever one you opened was missing half the answer.
 *
 * So there is one axis now, and it is the only one the operator is actually
 * choosing between: how much attention they are giving this. "At a glance" is
 * the answer in five lines and one simple picture. "In depth" is the full
 * treatment with an annotated picture and a walk through each part of it. Both
 * are complete; neither is a teaser for the other.
 *
 * The model supplies structure only (compose_answer in
 * cloud-anchor/pkg/nabxagent/tools.go); every visual decision is made here, so
 * there is no path from model output to markup.
 */

import React, { useState } from "react";
import { Diagram } from "./DiagramCard";
import { renderMarkdownish, inline } from "./markdownish";

const MODES = [
  { key: "glance", label: "At a glance", hint: "The answer in five lines" },
  { key: "depth", label: "In depth", hint: "The full explanation, annotated" },
];

/* A degraded call can leave a string where a diagram belongs; render what is
   usable rather than throwing the whole answer away. */
const isSpec = (v) => v && typeof v === "object" && !Array.isArray(v);

export default function TieredAnswer({ artifact }) {
  const [mode, setMode] = useState("glance");
  const spec = artifact.spec || {};

  /* Flat fields, deliberately. A nested shape reads better but the model
     serialises it unreliably at this depth — one wrapper level too many and
     nested objects came back as literal markup inside strings. The two depths
     are assembled here instead, where nothing can mis-serialise them. */
  const glance = {
    points: Array.isArray(spec.glance_points) ? spec.glance_points : [],
    note: typeof spec.glance_note === "string" ? spec.glance_note : "",
    diagram: isSpec(spec.glance_diagram) ? spec.glance_diagram : null,
  };
  const depth = {
    prose: typeof spec.depth_prose === "string" ? spec.depth_prose : "",
    note: typeof spec.depth_note === "string" ? spec.depth_note : "",
    diagram: isSpec(spec.depth_diagram) ? spec.depth_diagram : null,
    breakdown: Array.isArray(spec.depth_breakdown) ? spec.depth_breakdown : [],
  };
  const hasGlance = glance.points.length > 0 || glance.diagram;
  const hasDepth = depth.prose.trim().length > 0 || depth.diagram;
  if (!hasGlance && !hasDepth) return null;

  const available = MODES.filter((m) => (m.key === "glance" ? hasGlance : hasDepth));
  const active = available.some((m) => m.key === mode) ? mode : available[0].key;

  return (
    <div className="ta-card">
      <Styles />

      <div className="ta-head">
        <span className="ta-title">{artifact.title}</span>

        {available.length > 1 && (
          <div className="ta-switch" role="tablist" aria-label="How much detail">
            <span className="ta-thumb" data-at={active} aria-hidden="true" />
            {available.map((m) => (
              <button
                key={m.key}
                role="tab"
                aria-selected={m.key === active}
                title={m.hint}
                className={`ta-opt${m.key === active ? " is-on" : ""}`}
                onClick={() => setMode(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ta-body" key={active}>
        {active === "glance" ? (
          <>
            {glance.points.length > 0 && (
              <ul className="ta-points">
                {glance.points.slice(0, 5).map((line, i) => (
                  <li key={i} style={{ animationDelay: `${i * 50}ms` }}>{inline(String(line))}</li>
                ))}
              </ul>
            )}
            {glance.diagram && (
              <figure className="ta-fig">
                <Diagram spec={glance.diagram} />
                {glance.note && <figcaption>{inline(glance.note)}</figcaption>}
              </figure>
            )}
          </>
        ) : (
          <>
            {depth.prose && <div className="ta-prose">{renderMarkdownish(depth.prose)}</div>}
            {depth.diagram && (
              <figure className="ta-fig">
                <Diagram spec={depth.diagram} />
                {depth.note && <figcaption>{inline(depth.note)}</figcaption>}
              </figure>
            )}
            {/* Each block of the picture, explained in the picture's order —
                which is what makes the depth view worth opening rather than a
                longer version of the glance. */}
            {depth.breakdown.length > 0 && (
              <dl className="ta-break">
                {depth.breakdown.map((b, i) => (
                  <div className="ta-break-row" key={i} style={{ animationDelay: `${i * 40}ms` }}>
                    <dt>{b.label}</dt>
                    <dd>{inline(b.text)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Styles() {
  return (
    <style>{`
.ta-card{
  border:1px solid rgba(129,140,248,.28);border-radius:14px;overflow:hidden;
  background:linear-gradient(180deg,rgba(129,140,248,.07),rgba(255,255,255,.012));
}

.ta-head{
  display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap;
  padding:15px 16px 14px;border-bottom:1px solid rgba(255,255,255,.06);
}
.ta-title{font-size:16px;font-weight:600;color:#EDEFF6;letter-spacing:-.01em;min-width:0}

/* A two-way switch, not tabs: there are exactly two readings of the same
   answer, and a sliding thumb says that far better than a row of buttons. */
.ta-switch{
  position:relative;display:flex;flex:none;padding:3px;border-radius:999px;
  background:rgba(10,12,20,.55);border:1px solid rgba(255,255,255,.07);
}
.ta-thumb{
  position:absolute;top:3px;bottom:3px;width:calc(50% - 3px);border-radius:999px;
  background:linear-gradient(135deg,rgba(103,232,249,.20),rgba(129,140,248,.28));
  border:1px solid rgba(129,140,248,.45);
  transition:transform .22s cubic-bezier(.4,0,.2,1);
}
.ta-thumb[data-at="glance"]{transform:translateX(0)}
.ta-thumb[data-at="depth"]{transform:translateX(100%)}
.ta-opt{
  position:relative;z-index:1;cursor:pointer;border:0;background:none;
  padding:7px 15px;border-radius:999px;white-space:nowrap;
  font:600 12.5px/1 inherit;color:#8891AC;transition:color .18s;
}
.ta-opt.is-on{color:#F1F5F9}
.ta-opt:not(.is-on):hover{color:#C7D2FE}
.ta-opt:focus-visible{outline:2px solid #67E8F9;outline-offset:2px}

.ta-body{padding:16px;display:flex;flex-direction:column;gap:18px;animation:tafade .22s ease-out}
@keyframes tafade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}

.ta-points{margin:0;padding:0;list-style:none;display:flex;flex-direction:column;gap:10px}
.ta-points li{
  position:relative;padding-left:20px;font-size:14.5px;line-height:1.62;color:#C9CFE2;
  animation:tafade .3s ease-out both;
}
.ta-points li::before{
  content:"";position:absolute;left:3px;top:9px;width:6px;height:6px;border-radius:50%;
  background:linear-gradient(135deg,#67E8F9,#818CF8);
}
.ta-points strong{color:#fff;font-weight:650}

.ta-fig{margin:0;display:flex;flex-direction:column;gap:9px}
.ta-fig figcaption{font-size:13px;line-height:1.6;color:#8891AC}

.ta-prose{font-size:14.5px;line-height:1.68;color:#C9CFE2}
.ta-prose :first-child{margin-top:0}
.ta-prose :last-child{margin-bottom:0}

.ta-break{margin:0;display:flex;flex-direction:column;gap:1px;
  border:1px solid rgba(255,255,255,.07);border-radius:11px;overflow:hidden}
.ta-break-row{
  display:grid;grid-template-columns:minmax(120px,168px) 1fr;gap:16px;
  padding:12px 14px;background:rgba(255,255,255,.022);
  animation:tafade .3s ease-out both;
}
.ta-break-row dt{font-size:13px;font-weight:650;color:#E0E7FF}
.ta-break-row dd{margin:0;font-size:13.5px;line-height:1.6;color:#A8B0C6}

.ta-points code,.ta-fig code,.ta-prose code,.ta-break code{
  background:rgba(129,140,248,.14);border-radius:5px;padding:1px 5px;
  font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.9em;color:#C7D2FE;
}

@media (max-width:620px){
  .ta-break-row{grid-template-columns:1fr;gap:5px}
  .ta-switch{width:100%}
  .ta-opt{flex:1}
}
@media (prefers-reduced-motion:reduce){
  .ta-body,.ta-points li,.ta-break-row{animation:none}
  .ta-thumb{transition:none}
}
`}</style>
  );
}
